import React from 'react';
import { 
  Building2, 
  Brain, 
  DollarSign, 
  TrendingUp, 
  Clock, 
  BarChart, 
  Users, 
  FileText, 
  AlertCircle, 
  BarChart2 
} from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета стоимости внедрения ИИ
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Функция для форматирования денежных значений
  const formatMoney = (value) => {
    return formatCurrency(value, 0, results.currency);
  };

  // Форматирование периода окупаемости
  const formatPaybackPeriod = (years) => {
    const wholeYears = Math.floor(years);
    const months = Math.round((years - wholeYears) * 12);
    
    if (wholeYears === 0) {
      return `${months} мес.`;
    } else if (months === 0) {
      return `${wholeYears} ${formatYearsLabel(wholeYears)}`;
    } else {
      return `${wholeYears} ${formatYearsLabel(wholeYears)} ${months} мес.`;
    }
  };

  // Форматирование срока окупаемости по сравнению со средним по отрасли
  const getPaybackComparisonLabel = () => {
    const ratio = results.paybackPeriod / results.industryAvgPaybackPeriod;
    
    if (ratio <= 0.75) {
      return "Значительно быстрее среднего по отрасли";
    } else if (ratio <= 0.9) {
      return "Быстрее среднего по отрасли";
    } else if (ratio <= 1.1) {
      return "На уровне среднего по отрасли";
    } else if (ratio <= 1.25) {
      return "Дольше среднего по отрасли";
    } else {
      return "Значительно дольше среднего по отрасли";
    }
  };

  // Определение класса цвета для ROI
  const getRoiColorClass = (roi) => {
    if (roi >= 50) return 'text-green-600 dark:text-green-400';
    if (roi >= 20) return 'text-emerald-600 dark:text-emerald-400';
    if (roi >= 0) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Вспомогательная функция для склонения слова "год" в зависимости от числа
  const formatYearsLabel = (years) => {
    const lastDigit = years % 10;
    const lastTwoDigits = years % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
    if (lastDigit === 1) return 'год';
    if (lastDigit >= 2 && lastDigit <= 4) return 'года';
    return 'лет';
  };

  return (
    <ToolResult title="Результаты расчета стоимости внедрения ИИ">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая стоимость внедрения */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Общая стоимость внедрения
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatMoney(results.totalImplementationCost)}
            </span>
            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
              Включает все затраты на ИИ-решения, инфраструктуру и обучение
            </p>
          </div>
        </div>

        {/* Срок окупаемости и ROI */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Экономическая эффективность
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatPaybackPeriod(results.paybackPeriod)}
            </span>
            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
              срок окупаемости
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            ROI за 3 года: <span className={getRoiColorClass(results.threeYearROI)}>{formatNumber(results.threeYearROI, 1)}%</span>
          </p>
        </div>
      </div>

      {/* Дополнительные метрики */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Стоимость на студента</p>
            <p className="text-xl font-bold text-gray-800 dark:text-gray-200">{formatMoney(results.totalCostPerStudent)}</p>
          </div>
        </div>
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Ежегодные расходы</p>
            <p className="text-xl font-bold text-gray-800 dark:text-gray-200">{formatMoney(results.annualMaintenanceCost)}</p>
          </div>
        </div>
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Годовой эффект</p>
            <p className="text-xl font-bold text-gray-800 dark:text-gray-200">{formatMoney(results.annualBenefit)}</p>
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Базовые затраты" 
          value={formatMoney(results.baseCost)} 
          color="indigo" 
          icon={<Brain className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Затраты на инфраструктуру" 
          value={formatMoney(results.infrastructureCost)} 
          color="blue" 
          icon={<Building2 className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Внедрение и кастомизация" 
          value={formatMoney(results.implementationCost)} 
          color="purple" 
          icon={<FileText className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Обучение персонала" 
          value={formatMoney(results.trainingCost)} 
          color="emerald" 
          icon={<Users className="w-4 h-4"/>}
        />
        
        {results.dataPreparationCost > 0 && (
          <ResultItem 
            label="Подготовка данных" 
            value={formatMoney(results.dataPreparationCost)} 
            color="amber" 
            icon={<BarChart className="w-4 h-4"/>}
          />
        )}
        
        {results.consultingCost > 0 && (
          <ResultItem 
            label="Консультационные услуги" 
            value={formatMoney(results.consultingCost)} 
            color="pink" 
            icon={<BarChart2 className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Годовые затраты на поддержку" 
          value={formatMoney(results.annualMaintenanceCost)} 
          color="orange" 
          icon={<Clock className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Общая стоимость за 3 года" 
          value={formatMoney(results.totalThreeYearCost)} 
          color="rose" 
          icon={<DollarSign className="w-4 h-4"/>}
        />

        {/* Информация о сравнении с отраслевыми показателями */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Сравнение с отраслевыми показателями</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Стоимость на студента: <span className="font-medium">{formatMoney(results.totalCostPerStudent)}</span> 
                {results.totalCostPerStudent < results.industryAvgCostPerStudent 
                  ? ` (ниже среднего в ${formatNumber(results.industryAvgCostPerStudent / results.totalCostPerStudent, 1)} раза)` 
                  : ` (выше среднего в ${formatNumber(results.totalCostPerStudent / results.industryAvgCostPerStudent, 1)} раза)`}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Срок окупаемости: <span className="font-medium">{formatPaybackPeriod(results.paybackPeriod)}</span> — 
                <span className="italic ml-1">{getPaybackComparisonLabel()}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;